/* Offer widget behaviour. Selecting an option writes a variant id into
   the form's hidden input[name="id"] and a selling plan id, or nothing,
   into input[name="selling_plan"]. Those two are the whole submission.
   It also swaps the Add to cart button's price display between the
   SERVER-FORMATTED money strings the radio carries in its data
   attributes. Nothing here formats money and nothing computes a price;
   Liquid's | money already ran per variant and per allocation, which
   keeps Markets and multi-currency correct.

   Subscribe is controlled by the GLOBAL One-time/Subscribe tabs above the
   cards (the approved reference's design), not a per-card switch. Mode
   lives on THIS element as data-mode, which CSS also keys off to grey out
   a card that has no plan while Subscribe is active. Nothing here is
   computed: each card's subscribe price, compare-at and selling plan id
   are that card's own variant's own real selling_plan_allocations, read
   straight off data attributes nihon-hero-buy-option-v2 already writes,
   the same real numbers the old per-card row used. The tabs only change
   which of a card's two already-real prices is shown and submitted.

   sync() is the single writer and it derives EVERY output from the
   currently checked radio, this element's own mode, and that radio's own
   data attributes, so neither a card switch nor a tab switch can leave a
   stale plan id behind: both hidden inputs are rewritten unconditionally
   on every change, and the non-subscribing branch writes '' rather than
   skipping the write.

   A card whose variant carries no allocation is disabled outright while
   Subscribe is active (applyModeAvailability), the same treatment a
   sold-out card already gets, rather than left selectable with nothing
   real to charge. If the disabled card was the checked one, selection
   moves to the first still-selectable card so the form is never left
   pointed at a disabled radio. Flipping back to One-time re-enables every
   card except ones actually sold out.

   Variant media: THIS element owns it. Dawn drives its own gallery from
   product-info.js, which only runs inside a <product-info> element; the
   hero has no such element and no Dawn variant picker, so nothing of
   Dawn's is listening and there is nothing to double bind against. The
   selected option's featured media id is pushed into Dawn's gallery
   through its public setActiveMedia, so the gallery, its thumbnails and
   the lightbox all follow, with Dawn's snippets left exactly as they
   ship. A variant with no featured media leaves the gallery alone. */

class NihonHeroBuy extends HTMLElement {
  connectedCallback() {
    this.idInput = this.querySelector('input[name="id"]');
    this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    this.priceTarget = document.getElementById(`ProductSubmitPrice-${this.dataset.sectionId}`);
    this.gallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);

    /* The section computes the same choice server-side (default card's
       own subscribe_default field, only honoured when that card actually
       carries a plan) and paints every card's price as one-time
       regardless, so a subscribe-default page opens with a brief
       SSR-then-corrected window — the same tradeoff subscribe_default
       already accepted before the tabs existed. This is what corrects it,
       before the customer can interact with anything. */
    this.mode = this.dataset.initialMode === 'subscribe' ? 'subscribe' : 'one_time';
    this.dataset.mode = this.mode;

    this.addEventListener('click', (event) => {
      const tab = event.target.closest('.nihon-hero-buy__tab');
      if (tab) {
        this.setMode(tab.dataset.mode);
        return;
      }

      /* Click anywhere in the card selects it. Anything that already does
         something with a click is skipped: the label, the radio, the
         tabs and any other control or link, which is what stops a link in
         the include rows being swallowed. A sold-out or subscribe-
         disabled card's radio is disabled, so it stays unselectable. */
      if (event.target.closest('a, button, input, select, textarea, label')) return;
      const card = event.target.closest('.nihon-hero-buy__option');
      if (!card) return;
      const radio = card.querySelector('.nihon-hero-buy__option-input');
      if (!radio || radio.disabled || radio.checked) return;
      radio.checked = true;
      this.sync();
    });

    this.addEventListener('change', (event) => {
      if (event.target.closest('.nihon-hero-buy__option-input')) this.sync();
    });

    this.updateCardPrices();
    this.applyModeAvailability();
    this.sync({ media: false });
  }

  setMode(mode) {
    if (mode === this.mode) return;
    this.mode = mode;
    this.dataset.mode = mode;
    this.querySelectorAll('.nihon-hero-buy__tab').forEach((tab) => {
      const active = tab.dataset.mode === mode;
      tab.classList.toggle('active', active);
      tab.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this.updateCardPrices();
    this.applyModeAvailability();
    this.sync();
  }

  /* Every card shows its OWN price for the active mode, selected or not —
     the reference switches both cards at once. Display only, off the
     radio's own data attributes; nothing is computed here either. */
  updateCardPrices() {
    this.querySelectorAll('.nihon-hero-buy__option').forEach((card) => {
      const radio = card.querySelector('.nihon-hero-buy__option-input');
      const nowEl = card.querySelector('[data-price-now]');
      const wasEl = card.querySelector('[data-price-was]');
      if (!radio || !nowEl || !wasEl) return;

      const subscribing = this.mode === 'subscribe' && radio.dataset.hasPlan === 'true';
      const price = subscribing ? radio.dataset.subscribePrice : radio.dataset.price;
      const compare = subscribing ? radio.dataset.subscribeCompareAtPrice : radio.dataset.compareAtPrice;

      nowEl.textContent = price || '';
      if (compare) {
        wasEl.textContent = compare;
        wasEl.hidden = false;
      } else {
        wasEl.textContent = '';
        wasEl.hidden = true;
      }
    });
  }

  applyModeAvailability() {
    const cards = [...this.querySelectorAll('.nihon-hero-buy__option')];
    const checkedRadio = this.querySelector('.nihon-hero-buy__option-input:checked');
    let checkedNowDisabled = false;

    cards.forEach((card) => {
      const radio = card.querySelector('.nihon-hero-buy__option-input');
      // A sold-out radio is already permanently disabled; leave it alone.
      if (!radio || radio.dataset.soldout !== undefined) return;
      const unavailable = this.mode === 'subscribe' && radio.dataset.hasPlan !== 'true';
      radio.disabled = unavailable;
      if (unavailable && radio === checkedRadio) checkedNowDisabled = true;
    });

    if (checkedNowDisabled) {
      const fallback = cards
        .map((card) => card.querySelector('.nihon-hero-buy__option-input'))
        .find((radio) => radio && !radio.disabled);
      if (fallback) fallback.checked = true;
    }
  }

  sync({ media = true } = {}) {
    const radio = this.querySelector('.nihon-hero-buy__option-input:checked');
    if (!radio) return;

    const subscribing = this.mode === 'subscribe' && radio.dataset.hasPlan === 'true';

    if (this.idInput) this.idInput.value = radio.dataset.variantId;
    /* Always written, both ways round. A card with no plan, or the
       One-time tab, clears the field to '' rather than leaving whatever
       the previous mode or card put there. */
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = subscribing ? radio.dataset.sellingPlanId : '';
    }

    if (media) this.showVariantMedia(radio.dataset.featuredMediaId);

    if (!this.priceTarget) return;

    // Display only: every string below was formatted server-side by | money.
    const price = subscribing ? radio.dataset.subscribePrice : radio.dataset.price;
    const compare = subscribing ? radio.dataset.subscribeCompareAtPrice : radio.dataset.compareAtPrice;
    this.priceTarget.textContent = '';
    if (!price) return;

    const dash = document.createElement('span');
    dash.setAttribute('aria-hidden', 'true');
    dash.textContent = '–';
    const priceEl = document.createElement('span');
    priceEl.textContent = price;
    this.priceTarget.append(dash, priceEl);
    if (compare) {
      const compareEl = document.createElement('s');
      compareEl.textContent = compare;
      this.priceTarget.append(compareEl);
    }
  }

  /* Dawn's media-gallery keys its slides on `${sectionId}-${mediaId}`, and
     setActiveMedia is its own public entry point, the same one product-info
     calls on the default template. Guarded on every side: no gallery on the
     page, no media id on this variant, or an older gallery script without
     the method, and this quietly does nothing. */
  showVariantMedia(mediaId) {
    if (!this.gallery || !mediaId) return;
    this.gallery.setActiveMedia?.(`${this.dataset.sectionId}-${mediaId}`, true);
  }
}

customElements.define('nihon-hero-buy', NihonHeroBuy);
